import 'angular';
import * as _ from 'lodash-es';
import {DocMetadata} from "../types/DocMetadata";
import {FolderMetadata} from "../types/FolderMetadata";
import {ApprovalResource, ApprovalStatus, ApprovalBasicInfo, ApprovalLevelConfig} from "../approvals/approvals-types";
import {ApprovalRequestDTO, ApprovalLevelConfigDTO} from "../approvals/approvals-service-types"

export interface IApprovalUtils {
    isWorkDocsFolder: (resourceUri: string) => boolean;
    isWorkDocsFile: (resourceUri: string) => boolean;
    isWorkDocsResource: (resourceUri: string) => boolean;
    isDescriptionMarkdown : (description: string) => boolean;
    getFolderIdFromResourceUri: (resourceUri: string) => string;
    getDocumentAndVersionIds : (resourceuri: string) => {documentId: string, versionId: string};
    folderMetadata2ApprovalResource: (folder: FolderMetadata) => ApprovalResource;
    fileMetadata2ApprovalResource: (doc: DocMetadata) => ApprovalResource;
    getApprovalStatusText: (approvalStatus: ApprovalStatus) => string;
    getApprovalStatusIndicator: (approvalStatus: ApprovalStatus) => string;
    composeApprovalRequestDTO: (basicInfo: ApprovalBasicInfo, levelConfigs: ApprovalLevelConfig[]) => ApprovalRequestDTO;
    approvalLevelConfig2ApprovalLevelConfigDTO: (levelConfig: ApprovalLevelConfig) => ApprovalLevelConfigDTO;
};

angular.module('GorillaBoy.Approvals')
    .factory('ApprovalUtils', ['$translate', factoryFn]);

function factoryFn($translate: ng.translate.ITranslateService) : IApprovalUtils {
    const WORKDOCS_FILE_REGEXP = /workdocs:\/\/file\/([0-9a-z-]+)\/version\/([0-9a-z-]+)/i;
    const WORKDOCS_FOLDER_REGEXP = /workdocs:\/\/folder\/([0-9a-z-]+)/i;

    function isWorkDocsFolder(resourceUri: string) : boolean {
        return WORKDOCS_FOLDER_REGEXP.test(resourceUri);
    }

    function isWorkDocsFile(resourceUri: string) : boolean {
        return WORKDOCS_FILE_REGEXP.test(resourceUri);
    }

    function isWorkDocsResource(resourceUri: string) : boolean {
        return isWorkDocsFile(resourceUri) || isWorkDocsFolder(resourceUri);
    }

    function isDescriptionMarkdown(description: string) : boolean {
        return _.startsWith(description, '/md');
    }

    function getFolderIdFromResourceUri(resourceuri: string) : string {
        if (!isWorkDocsResource(resourceuri)) {
            throw Error("Invalid argument passed, the resourceUri is not of WorkDocs folder type");
        }
        let regExpMatchArray = resourceuri.match(WORKDOCS_FOLDER_REGEXP);
        return regExpMatchArray[1];
    }

    function getDocumentAndVersionIds(resourceuri: string) : {documentId: string, versionId: string} {
        if (!isWorkDocsResource(resourceuri)) {
            throw Error("Invalid argument passed, the resourceUri is not of WorkDocs document type");
        }
        let regExpMatchArray = resourceuri.match(WORKDOCS_FILE_REGEXP);
        return {documentId: regExpMatchArray[1], versionId: regExpMatchArray[2]};
    }

    function folderMetadata2ApprovalResource(folder: FolderMetadata) {
        let resource: ApprovalResource = {
            ResourceUri: 'workdocs://folder/' + folder.Id,
            ResourceKind: "WorkdocsFolder",
            ResourceName: folder.Name,
            Properties: []
        }
        return resource;
    }

    function fileMetadata2ApprovalResource(doc: DocMetadata) {
        let resource: ApprovalResource = {
            ResourceUri: 'workdocs://file/' + doc.Id + '/version/' + doc.VersionMetadata.VersionId,
            ResourceKind: "WorkdocsFile",
            ResourceName: doc.VersionMetadata.Name,
            Properties: []
        };
        return resource;
    }

    function getApprovalStatusText(levelStatus: ApprovalStatus) {
        switch (levelStatus) {
            case ApprovalStatus.APPROVED:
                return $translate.instant('APPROVALS.VIEWER.GENERAL_STATUS_LABEL.APPROVED');
            case ApprovalStatus.REJECTED:
                return $translate.instant('APPROVALS.VIEWER.GENERAL_STATUS_LABEL.REJECTED');
            case ApprovalStatus.PENDING:
                return $translate.instant('APPROVALS.VIEWER.GENERAL_STATUS_LABEL.PENDING');
            case ApprovalStatus.QUEUED:
                return $translate.instant('APPROVALS.VIEWER.GENERAL_STATUS_LABEL.QUEUED');
            default:
                return null;
        }
    }

    function getApprovalStatusIndicator(levelStatus: ApprovalStatus) {
        switch (levelStatus) {
            case ApprovalStatus.APPROVED:
                return 'approval-approved-indicator';
            case ApprovalStatus.REJECTED:
                return 'approval-rejected-indicator';
            case ApprovalStatus.PENDING:
            case ApprovalStatus.QUEUED:
                return 'approval-pending-indicator';
            default:
                return null;
        }
    }

    function approvalLevelConfig2ApprovalLevelConfigDTO(levelConfig: ApprovalLevelConfig): ApprovalLevelConfigDTO {
        let result: ApprovalLevelConfigDTO = {
            LevelNumber: levelConfig.LevelNumber,
            DueDate: levelConfig.DueDate,
            RemindersInSeconds: levelConfig.RemindersInSeconds,
            CompleteDate: levelConfig.CompleteDate,
            MinimumApprovalsRequired: levelConfig.MinimumApprovalsRequired
        }

        return result;
    }

    function composeApprovalRequestDTO(basicInfo: ApprovalBasicInfo, levelConfigs: ApprovalLevelConfig[]): ApprovalRequestDTO {
        let result: ApprovalRequestDTO = {
            Title: basicInfo.Title,
            Description: basicInfo.Description,
            LevelConfigs: levelConfigs.map(approvalLevelConfig2ApprovalLevelConfigDTO),
            Properties: basicInfo.Properties
        }

        return result;
    }

    return {
        isWorkDocsFolder,
        isWorkDocsFile,
        isDescriptionMarkdown,
        isWorkDocsResource,
        getFolderIdFromResourceUri,
        getDocumentAndVersionIds,
        folderMetadata2ApprovalResource,
        fileMetadata2ApprovalResource,
        getApprovalStatusText,
        getApprovalStatusIndicator,
        composeApprovalRequestDTO,
        approvalLevelConfig2ApprovalLevelConfigDTO,
    };
}
